import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, Info } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { SELECT_SERVICE_EVENT, SERVICE_AVAILABILITY, getCampaignParams, track } from "../lib/funnel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;
const SERVICE_LABELS = { import: "GTX IMPORT", europe: "GTX EUROPE ENTRY", buy: "GTX BUY FOR YOU", unsure: "Sin definir / Unsure" };

const inputCls = "bg-white border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg h-11 px-3.5 text-sm text-slate-900 placeholder:text-slate-600 transition-colors";
const selectCls = inputCls + " w-full border appearance-none cursor-pointer";
const textareaCls = "bg-white border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 rounded-lg px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-600 min-h-[96px] transition-colors";

const initialData = {
  is_business: "", service: "", country: "",
  category: "", product: "", origin_country: "", destination_country: "", value_eur: "", quantity: "", unit: "", has_supplier: "", timeline: "",
  destination_countries: "", eu_clients: "", coordination: [], value_volume: "",
  condition: "", extra_needs: "",
  company: "", name: "", position: "", email: "", phone: "", comment: "", source: "",
  accept_privacy: false, accept_marketing: false, website: "",
};

export function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mono">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function Chips({ options, value, onChange, testPrefix, multi = false }) {
  const isActive = (o) => (multi ? value.includes(o) : value === o);
  const toggle = (o) => {
    if (!multi) return onChange(o);
    onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  };
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          onClick={() => toggle(o)}
          className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors duration-200 ${
            isActive(o) ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400 hover:text-slate-900"
          }`}
          data-testid={`${testPrefix}-${o}`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function Select({ name, value, onChange, placeholder, options, testId, required }) {
  return (
    <select name={name} required={required} value={value} onChange={onChange} className={selectCls} data-testid={testId}>
      <option value="" className="bg-white">{placeholder}</option>
      {options.map((c) => (
        <option key={c} value={c} className="bg-white">{c}</option>
      ))}
    </select>
  );
}

export default function LeadForm({ getWA, whatsappHome }) {
  const { t, language } = useLanguage();
  const [data, setData] = useState(initialData);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    const onSelect = (e) => {
      setData((p) => ({ ...p, service: e.detail }));
      setStep(1);
      setSuccess(null);
    };
    window.addEventListener(SELECT_SERVICE_EVENT, onSelect);
    return () => window.removeEventListener(SELECT_SERVICE_EVENT, onSelect);
  }, []);

  const set = (name, value) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track.formStart(data.service);
    }
    setData((p) => ({ ...p, [name]: value }));
  };
  const handle = (e) => {
    const { name, value, type, checked } = e.target;
    set(name, type === "checkbox" ? checked : value);
  };

  const svc = data.service;
  const isInterest = svc === "buy" && SERVICE_AVAILABILITY.buy !== "available";
  const isPrivate = data.is_business === "No";

  const step1Valid = () => {
    if (!data.is_business || !svc || !data.country) return false;
    if (svc === "import") return data.category && data.product && data.origin_country && data.destination_country && data.has_supplier && data.timeline;
    if (svc === "europe") return data.origin_country && data.product && data.destination_countries && data.eu_clients && data.timeline;
    if (svc === "buy") return data.product && data.destination_country;
    return true;
  };

  const goNext = (e) => {
    e.preventDefault();
    if (!step1Valid()) {
      setError(t("contact.errorRequired"));
      return;
    }
    setError("");
    setStep(2);
    document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (data.website) return;
    if (!data.company || !data.name || !data.email || !data.phone) {
      setError(t("contact.errorRequired"));
      return;
    }
    if (!data.accept_privacy) {
      setError(t("contact.errorPrivacy"));
      return;
    }
    setSubmitting(true);
    const leadKind = isPrivate ? "not_qualified" : isInterest ? "interest" : "evaluation";
    const destination = svc === "europe" ? data.destination_countries : data.destination_country;
    const quantity = [data.quantity, data.unit].filter(Boolean).join(" ") || data.value_volume || "";
    const messageParts = [data.comment, data.extra_needs ? `${t("contact.fExtraNeeds")}: ${data.extra_needs}` : ""].filter(Boolean);
    const payload = {
      name: data.name, company: data.company, phone: data.phone, email: data.email, position: data.position,
      product: data.product || SERVICE_LABELS[svc], quantity, origin: data.origin_country, destination: destination || data.country,
      service_type: SERVICE_LABELS[svc] || svc, brands: data.category, frequency: "", source: data.source,
      eu_clients: data.eu_clients, deadline: data.timeline, source_country: data.origin_country,
      accept_marketing: data.accept_marketing, message: messageParts.join("\n") || "-", language,
      is_business: data.is_business, lead_kind: leadKind, category: data.category, value_eur: data.value_eur,
      has_supplier: data.has_supplier, coordination_needs: data.coordination.join(", "), product_condition: data.condition,
      ...getCampaignParams(),
    };
    try {
      await axios.post(`${API}/leads`, payload);
      if (leadKind === "interest") track.interestRegistered(svc);
      else if (leadKind === "evaluation") track.evaluationSubmitted(svc);
      setSuccess(leadKind);
    } catch (err) {
      setError(t("contact.errorSubmit"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = isInterest ? t("contact.submitInterest") : svc === "unsure" || !svc ? t("contact.submitGeneric") : t("contact.submitEvaluation");

  if (success) {
    const interest = success === "interest";
    return (
      <div className="py-10 text-center" data-testid="form-success">
        <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-300 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-3">{interest ? t("contact.successInterestTitle") : t("contact.successTitle")}</h3>
        <p className="text-slate-600 max-w-md mx-auto">{interest ? t("contact.successInterestMessage") : t("contact.successMessage")}</p>
        {!interest && svc === "import" && (
          <div className="mt-7 space-y-3" data-testid="success-calendly-block">
            <p className="text-sm text-slate-700">{t("contact.successImportExtra")}</p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" onClick={() => track.calendlyClick("success")} data-testid="success-calendly-btn">
              <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-lg px-6 h-11 font-medium gap-2 shadow-lg shadow-blue-600/25">
                <CalendarDays className="w-4 h-4" />
                {t("contact.successCalendly")}
              </Button>
            </a>
          </div>
        )}
        {!interest && svc !== "import" && <p className="mt-6 text-sm text-slate-500">{t("contact.successOtherExtra")}</p>}
      </div>
    );
  }

  return (
    <form onSubmit={step === 1 ? goNext : submit} className="space-y-5" data-testid="contact-form" noValidate>
      <div className="flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.18em] mono" data-testid="form-stepper">
        <span className={step === 1 ? "text-blue-600" : "text-slate-500"}>{t("contact.step1")}</span>
        <span className="h-px flex-1 bg-slate-100" />
        <span className={step === 2 ? "text-blue-600" : "text-slate-500"}>{t("contact.step2")}</span>
      </div>

      {isInterest && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-[11px] font-bold uppercase tracking-[0.14em] mono" data-testid="form-interest-badge">
          {t("contact.interestBadge")}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-4" data-testid="form-step-1">
          <Field label={t("contact.fIsBusiness") + " *"}>
            <Chips options={[t("contact.yes"), t("contact.no")]} value={data.is_business === "Sí" ? t("contact.yes") : data.is_business === "No" ? t("contact.no") : ""} onChange={(v) => set("is_business", v === t("contact.no") ? "No" : "Sí")} testPrefix="f-business" />
          </Field>
          {isPrivate && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-md p-3" data-testid="not-business-notice">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              {t("contact.notBusinessNotice")}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("contact.fService") + " *"}>
              <select name="service" value={svc} onChange={handle} className={selectCls} data-testid="f-service">
                <option value="" className="bg-white">{t("contact.servicePlaceholder")}</option>
                {t("contact.serviceOptions").map((o) => (
                  <option key={o.id} value={o.id} className="bg-white">{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label={t("contact.fCountry") + " *"}>
              <Select name="country" value={data.country} onChange={handle} placeholder={t("contact.countryPlaceholder")} options={t("contact.countries")} testId="f-country" />
            </Field>
          </div>

          {svc === "import" && (
            <div className="space-y-4" data-testid="fields-import">
              <Field label={t("contact.fCategory") + " *"}>
                <Chips options={t("contact.categories")} value={data.category} onChange={(v) => set("category", v)} testPrefix="f-category" />
              </Field>
              <Field label={t("contact.fProduct") + " *"}>
                <Input name="product" value={data.product} onChange={handle} className={inputCls} data-testid="f-product" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t("contact.fOrigin") + " *"}>
                  <Input name="origin_country" value={data.origin_country} onChange={handle} className={inputCls} placeholder="Hong Kong, Dubái, EE.UU., ..." data-testid="f-origin" />
                </Field>
                <Field label={t("contact.fDestination") + " *"}>
                  <Select name="destination_country" value={data.destination_country} onChange={handle} placeholder={t("contact.countryPlaceholder")} options={t("contact.countries").filter((c) => !/Fuera|Outside|Reino Unido|United Kingdom/.test(c))} testId="f-destination" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label={t("contact.fValue")}>
                  <Input name="value_eur" inputMode="numeric" value={data.value_eur} onChange={handle} className={inputCls} placeholder="€" data-testid="f-value" />
                </Field>
                <Field label={t("contact.fQuantity")}>
                  <Input name="quantity" value={data.quantity} onChange={handle} className={inputCls} data-testid="f-quantity" />
                </Field>
                <Field label={t("contact.fUnit")}>
                  <Select name="unit" value={data.unit} onChange={handle} placeholder="—" options={t("contact.units")} testId="f-unit" />
                </Field>
              </div>
              <Field label={t("contact.fHasSupplier") + " *"}>
                <Chips options={[t("contact.yes"), t("contact.no"), t("contact.negotiating")]} value={data.has_supplier} onChange={(v) => set("has_supplier", v)} testPrefix="f-supplier" />
              </Field>
              {data.has_supplier === t("contact.no") && (
                <div className="text-sm text-slate-700 bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2" data-testid="no-supplier-notice">
                  <p>{t("contact.noSupplierNotice")}</p>
                  <button type="button" onClick={() => set("service", "buy")} className="text-blue-700 underline underline-offset-2 text-sm font-semibold" data-testid="no-supplier-link">
                    {t("contact.noSupplierLink")} →
                  </button>
                </div>
              )}
              <Field label={t("contact.fTimeline") + " *"}>
                <Chips options={t("contact.timelines")} value={data.timeline} onChange={(v) => set("timeline", v)} testPrefix="f-timeline" />
              </Field>
            </div>
          )}

          {svc === "europe" && (
            <div className="space-y-4" data-testid="fields-europe">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t("contact.fOriginGoods") + " *"}>
                  <Input name="origin_country" value={data.origin_country} onChange={handle} className={inputCls} data-testid="f-origin" />
                </Field>
                <Field label={t("contact.fProductCategory") + " *"}>
                  <Input name="product" value={data.product} onChange={handle} className={inputCls} data-testid="f-product" />
                </Field>
              </div>
              <Field label={t("contact.fEuDestinations") + " *"}>
                <Input name="destination_countries" value={data.destination_countries} onChange={handle} className={inputCls} placeholder="España, Alemania, Francia..." data-testid="f-eu-destinations" />
              </Field>
              <Field label={t("contact.fEuClients") + " *"}>
                <Chips options={[t("contact.yes"), t("contact.no"), t("contact.negotiating")]} value={data.eu_clients} onChange={(v) => set("eu_clients", v)} testPrefix="f-eu-clients" />
              </Field>
              <Field label={t("contact.fCoordination")}>
                <Chips multi options={t("contact.coordinationOptions")} value={data.coordination} onChange={(v) => set("coordination", v)} testPrefix="f-coord" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t("contact.fValueVolume")}>
                  <Input name="value_volume" value={data.value_volume} onChange={handle} className={inputCls} data-testid="f-value-volume" />
                </Field>
              </div>
              <Field label={t("contact.fTimeline") + " *"}>
                <Chips options={t("contact.timelines")} value={data.timeline} onChange={(v) => set("timeline", v)} testPrefix="f-timeline" />
              </Field>
            </div>
          )}

          {svc === "buy" && (
            <div className="space-y-4" data-testid="fields-buy">
              <Field label={t("contact.fProductBrand") + " *"}>
                <Input name="product" value={data.product} onChange={handle} className={inputCls} placeholder="iPhone, Galaxy, iPad..." data-testid="f-product" />
              </Field>
              <Field label={t("contact.fCondition")}>
                <Chips options={t("contact.conditions")} value={data.condition} onChange={(v) => set("condition", v)} testPrefix="f-condition" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t("contact.fDesiredQty")}>
                  <Input name="quantity" value={data.quantity} onChange={handle} className={inputCls} data-testid="f-quantity" />
                </Field>
                <Field label={t("contact.fDestCountry") + " *"}>
                  <Select name="destination_country" value={data.destination_country} onChange={handle} placeholder={t("contact.countryPlaceholder")} options={t("contact.countries")} testId="f-destination" />
                </Field>
              </div>
              <Field label={t("contact.fIndicativeTimeline")}>
                <Chips options={t("contact.timelines")} value={data.timeline} onChange={(v) => set("timeline", v)} testPrefix="f-timeline" />
              </Field>
              <Field label={t("contact.fExtraNeeds")}>
                <Textarea name="extra_needs" maxLength={500} value={data.extra_needs} onChange={handle} className={textareaCls} data-testid="f-extra-needs" />
              </Field>
            </div>
          )}

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="form-error">{error}</div>}
          <p className="text-xs text-slate-500">{t("contact.requiredHint")}</p>
          <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-lg py-6 text-base font-medium gap-2 shadow-lg shadow-blue-600/25 active:scale-[0.98]" data-testid="form-next-btn">
            {t("contact.next")}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="form-step-2">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("contact.fCompany") + " *"}>
              <Input name="company" value={data.company} onChange={handle} className={inputCls} data-testid="f-company" />
            </Field>
            <Field label={t("contact.fName") + " *"}>
              <Input name="name" value={data.name} onChange={handle} className={inputCls} data-testid="f-name" />
            </Field>
            <Field label={t("contact.fPosition")}>
              <Input name="position" value={data.position} onChange={handle} className={inputCls} data-testid="f-position" />
            </Field>
            <Field label={t("contact.fEmail") + " *"}>
              <Input type="email" name="email" value={data.email} onChange={handle} className={inputCls} data-testid="f-email" />
            </Field>
            <Field label={t("contact.fPhone") + " *"}>
              <Input type="tel" name="phone" value={data.phone} onChange={handle} className={inputCls} placeholder="+34 600 000 000" data-testid="f-phone" />
            </Field>
            <Field label={t("contact.fSource")}>
              <Select name="source" value={data.source} onChange={handle} placeholder={t("contact.sourcePlaceholder")} options={t("contact.sources")} testId="f-source" />
            </Field>
          </div>
          <Field label={t("contact.fComment")}>
            <Textarea name="comment" maxLength={500} value={data.comment} onChange={handle} className={textareaCls} data-testid="f-message" />
          </Field>

          <input type="text" name="website" value={data.website} onChange={handle} className="hidden" tabIndex="-1" autoComplete="off" />

          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer pt-2">
            <input type="checkbox" name="accept_privacy" checked={data.accept_privacy} onChange={handle} className="mt-1 w-4 h-4 accent-blue-500" data-testid="f-accept-privacy" />
            <span>
              {t("contact.fAcceptPrivacy")} *{" "}
              <Link to="/privacy" className="text-blue-600 underline hover:text-blue-700">({t("footer.privacy")})</Link>
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" name="accept_marketing" checked={data.accept_marketing} onChange={handle} className="mt-1 w-4 h-4 accent-blue-500" data-testid="f-accept-marketing" />
            <span>{t("contact.fAcceptMarketing")}</span>
          </label>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="form-error">{error}</div>}
          <p className="text-xs text-slate-500">{t("contact.requiredHint")}</p>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button type="button" variant="outline" onClick={() => { setError(""); setStep(1); }} className="sm:w-auto rounded-lg py-6 border-slate-300 bg-white text-slate-800 hover:bg-slate-50 gap-2" data-testid="form-back-btn">
              <ArrowLeft className="w-4 h-4" />
              {t("contact.back")}
            </Button>
            <Button type="submit" disabled={submitting} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg py-6 text-base font-medium shadow-lg shadow-blue-600/25 active:scale-[0.98]" data-testid="contact-submit-btn">
              {submitting ? t("contact.submitting") : submitLabel}
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
